import {
  AISearchRecord,
  DatasetName,
  StoredBrand,
  StoredBrandSuggestion,
  WorkspaceItem,
  getAllFromStore,
  getByKey,
  putDataset,
  putRecord,
  putRecords,
} from './indexedDB';
import {
  mockDomainMovers,
  mockDomainTypeShares,
  mockEarnedData,
  mockGapAnalysisData,
  mockInsightsData,
  mockImpactData,
  mockOwnedData,
  mockOverviewData,
  mockProfileData,
  mockPromptsData,
  mockSourceDomains,
  mockSourceUrls,
  mockBrandsData,
  domesticLLMs,
  internationalLLMs,
} from '../mockData';
import {
  mockBrandProfileByWorkspace,
  mockBrandProfileDefault,
} from '../mockData/brandProfile';
import {
  earnedInitialRecommendations,
  huaxingEarnedInitialRecommendations,
  ownedInitialRecommendations,
} from './recommendationSeeds';
import {
  mockEarnedByCompany,
  mockImpactByCompany,
  mockOverviewByCompany,
  mockProfileByCompany,
  mockPromptsByCompany,
  mockSourceDomainsByCompany,
  mockUrlsByCompany,
  mockWorkspaces,
} from '../mockData/workspaces';
import { Brand, SourceUrlRow } from '../types';

const SEED_VERSION = '2026-06-23-web3-v4';

const domesticManufacturingWorkspace: WorkspaceItem = {
  id: 'workspace_huaxing_precision',
  name: '华星精工',
  domain: 'huaxing-precision.cn',
  initials: 'hx',
  plan: 'professional',
};

const domesticManufacturingBrand: Brand = {
  id: 'brand_huaxing_precision',
  name: '华星精工',
  display_name: '华星精工',
  tracked_names: ['华星精工', 'Huaxing Precision', '华星精密制造'],
  domains: ['huaxing-precision.cn'],
  mentions: 128,
  color: 'emerald',
  is_own: true,
  is_active: true,
};

const manufacturingUrls: SourceUrlRow[] = [
  {
    id: 'hx_url_1',
    url: 'https://huaxing-precision.cn/capabilities/cnc-machining',
    title: '五轴 CNC 精密加工能力',
    domain: 'huaxing-precision.cn',
    domain_type: 'Corporate',
    retrieved: 26.8,
    retrieval_rate: 2.4,
    citation_rate: 1.7,
    mentions: 43,
    gap_score: 42,
  },
  {
    id: 'hx_url_2',
    url: 'https://huaxing-precision.cn/quality/iatf16949',
    title: 'IATF 16949 汽车零部件质量体系',
    domain: 'huaxing-precision.cn',
    domain_type: 'Corporate',
    retrieved: 21.2,
    retrieval_rate: 1.9,
    citation_rate: 1.4,
    mentions: 35,
    gap_score: 39,
  },
  {
    id: 'hx_url_3',
    url: 'https://www.miit.gov.cn/search?keyword=%E7%B2%BE%E5%AF%86%E5%88%B6%E9%80%A0',
    title: '工信部精密制造产业政策资料',
    domain: 'miit.gov.cn',
    domain_type: 'Institutional',
    retrieved: 18.5,
    retrieval_rate: 1.5,
    citation_rate: 1.2,
    mentions: 18,
    gap_score: 58,
  },
];

const workspaceIds = [
  ...mockWorkspaces.map((workspace) => workspace.id),
  domesticManufacturingWorkspace.id,
];

const withWorkspaceBrands = (workspaceId: string): StoredBrand[] => {
  const now = new Date().toISOString();
  const brands = workspaceId === domesticManufacturingWorkspace.id
    ? [
      domesticManufacturingBrand,
      {
        id: 'brand_haitian',
        name: '海天精工',
        display_name: '海天精工',
        tracked_names: ['海天精工', 'Haitian Precision'],
        domains: ['haitianprecision.com'],
        mentions: 86,
        color: 'sky',
        is_active: true,
      },
      {
        id: 'brand_hikrobot',
        name: '海康机器人',
        display_name: '海康机器人',
        tracked_names: ['海康机器人', 'Hikrobot'],
        domains: ['hikrobotics.com'],
        mentions: 74,
        color: 'violet',
        is_active: true,
      },
    ]
    : mockBrandsData.data;

  return brands.map((brand) => ({
    ...brand,
    id: `${workspaceId}:${brand.id}`,
    workspaceId,
    created_at: now,
    updated_at: now,
  }));
};

const withWorkspaceSuggestions = (workspaceId: string): StoredBrandSuggestion[] => {
  const now = new Date().toISOString();
  return mockBrandsData.suggestions.map((suggestion) => ({
    ...suggestion,
    id: `${workspaceId}:${suggestion.id}`,
    workspaceId,
    created_at: now,
    updated_at: now,
  }));
};

const createAISearchRecord = (
  workspaceId: string,
  row: SourceUrlRow,
  index: number,
  options?: Partial<AISearchRecord>
): AISearchRecord => ({
  id: `${workspaceId}:ai_record_${index + 1}`,
  workspaceId,
  prompt: options?.prompt ?? '哪些品牌适合高精度零部件和智能制造产线升级？',
  ai: options?.ai ?? ['DeepSeek', '通义千问', 'Kimi'][index % 3],
  domain: options?.domain ?? row.domain,
  domain_type: options?.domain_type ?? row.domain_type,
  brand: options?.brand ?? domesticManufacturingBrand.display_name,
  brand_list: options?.brand_list ?? [domesticManufacturingBrand.display_name, '海天精工', '海康机器人'],
  title: options?.title ?? row.title,
  response_json: options?.response_json ?? {
    summary: `${row.title} 被 AI 回答用于支撑品牌与制造能力判断。`,
    mentioned_brand: domesticManufacturingBrand.display_name,
    location: 'CN',
    confidence: 0.78 + index * 0.04,
  },
  url_list: options?.url_list ?? manufacturingUrls.map((url) => url.url),
  url_type_list: options?.url_type_list ?? manufacturingUrls.map((url) => url.domain_type),
  timestamp: options?.timestamp ?? new Date(Date.UTC(2026, 4, 31, 8 + index, 15, 0)).toISOString(),
});

const createWorkspaceAISearchRecords = (workspaceId: string): AISearchRecord[] => {
  if (workspaceId === domesticManufacturingWorkspace.id) {
    return manufacturingUrls.map((url, index) => createAISearchRecord(workspaceId, url, index, {
      brand: domesticManufacturingBrand.display_name,
      brand_list: [
        [domesticManufacturingBrand.display_name, '海天精工', '海康机器人'],
        [domesticManufacturingBrand.display_name, '海天精工'],
        [domesticManufacturingBrand.display_name],
      ][index] ?? [domesticManufacturingBrand.display_name],
    }));
  }

  const urls = mockUrlsByCompany[workspaceId] ?? mockSourceUrls;
  const overview = mockOverviewByCompany[workspaceId] ?? mockOverviewData;
  const overviewBrands = (overview.top_brands ?? []) as Array<{ name: string }>;
  const brandNames = overviewBrands.map((brand) => brand.name).filter(Boolean);
  const fallbackBrands = mockBrandsData.data.map((brand) => brand.display_name);
  const availableBrands = brandNames.length > 0 ? brandNames : fallbackBrands;

  return urls.map((url, index) => {
    const primaryBrand = availableBrands[index % availableBrands.length];
    const secondaryBrand = availableBrands[(index + 1) % availableBrands.length];
    const tertiaryBrand = availableBrands[(index + 2) % availableBrands.length];
    const brandList = index % 3 === 0
      ? [primaryBrand, secondaryBrand, tertiaryBrand]
      : index % 3 === 1
        ? [primaryBrand, secondaryBrand]
        : [primaryBrand];

    return createAISearchRecord(workspaceId, url, index, {
      prompt: (mockPromptsByCompany[workspaceId]?.data?.[index % (mockPromptsByCompany[workspaceId]?.data?.length || 1)]?.title)
        ?? 'Which brands are most visible in AI answers for this category?',
      ai: ['ChatGPT', 'Claude 3', 'Perplexity', 'Gemini', 'Kimi'][index % 5],
      brand: primaryBrand,
      brand_list: brandList,
      response_json: {
        summary: `${url.title} appeared as supporting evidence in an AI answer about ${primaryBrand}.`,
        mentioned_brand: primaryBrand,
        location: mockPromptsByCompany[workspaceId]?.data?.[0]?.location ?? 'US',
        confidence: 0.72 + index * 0.03,
      },
      url_list: urls.map((sourceUrl) => sourceUrl.url),
      url_type_list: urls.map((sourceUrl) => sourceUrl.domain_type),
      timestamp: new Date(Date.UTC(2026, 4, 31, 7 + index, 10, 0)).toISOString(),
    });
  });
};

const buildDataset = (workspaceId: string, name: DatasetName) => {
  if (workspaceId === domesticManufacturingWorkspace.id) {
    switch (name) {
      case 'overview':
        return {
          ...mockOverviewData,
          workspace_name: domesticManufacturingWorkspace.name,
          total_domains: 96,
          total_urls: 418,
          total_prompts: 32,
          top_domains: [
            { id: 'hx_domain_1', rank: 1, domain: 'huaxing-precision.cn', domain_type: 'Corporate', retrievals: 268, retrieved: 42.2, retrieval_rate: 2.6, citation_rate: 1.8 },
            { id: 'hx_domain_2', rank: 2, domain: 'miit.gov.cn', domain_type: 'Institutional', retrievals: 183, retrieved: 31.4, retrieval_rate: 1.9, citation_rate: 1.3 },
          ],
          domain_types: mockDomainTypeShares,
          account_quick_overview: {
            brand_name: domesticManufacturingBrand.display_name,
            brand_icon_url: `https://www.google.com/s2/favicons?domain=${domesticManufacturingWorkspace.domain}&sz=64`,
            period_label: '过去一周',
            visibility_delta: { value: '+18.6%', direction: 'up' },
            topic: { name: '精密制造供应商', average_rank: 2.8 },
            platform: { name: 'DeepSeek', highlight: '在国产制造能力问答中被高频提及' },
          },
        };
      case 'prompts':
        return {
          total: 3,
          page: 1,
          limit: 50,
          topics: [
            { id: 'hx_topic_1', name: '精密制造供应商', prompt_count: 2 },
            { id: 'hx_topic_2', name: '智能制造产线', prompt_count: 1 },
          ],
          data: [
            {
              id: 'hx_prompt_1',
              title: '国内有哪些适合汽车零部件的精密制造供应商？',
              description: '跟踪 AI 对国内精密制造品牌和质量体系的推荐。',
              created_at: '2026-05-31T09:00:00Z',
              used_count: 36,
              visibility: 48,
              sentiment: 76,
              position: 2.8,
              mentions: 14,
              volume: 1800,
              tags: ['制造业', '汽车零部件'],
              location: 'CN',
              sov: 18,
              web_search: true,
              topic: '精密制造供应商',
            },
            {
              id: 'hx_prompt_2',
              title: '五轴 CNC 加工厂家怎么选？',
              description: '覆盖设备能力、交付周期、质检体系和出口经验。',
              created_at: '2026-05-30T09:00:00Z',
              used_count: 28,
              visibility: 39,
              sentiment: 72,
              position: 3.4,
              mentions: 9,
              volume: 1200,
              tags: ['CNC', '五轴加工'],
              location: 'CN',
              sov: 14,
              web_search: true,
              topic: '精密制造供应商',
            },
          ],
        };
      case 'domains':
        return {
          total: 2,
          page: 1,
          limit: 20,
          data: [
            { id: 'hx_domain_1', rank: 1, domain: 'huaxing-precision.cn', domain_type: 'Corporate', retrievals: 268, retrieved: 42.2, retrieval_rate: 2.6, citation_rate: 1.8 },
            { id: 'hx_domain_2', rank: 2, domain: 'miit.gov.cn', domain_type: 'Institutional', retrievals: 183, retrieved: 31.4, retrieval_rate: 1.9, citation_rate: 1.3 },
          ],
          domain_types: mockDomainTypeShares,
        };
      case 'urls':
        return { total: manufacturingUrls.length, page: 1, limit: 50, data: manufacturingUrls, domain_types: mockDomainTypeShares };
      case 'brandProfile':
        return {
          ...mockBrandProfileDefault,
          companyName: domesticManufacturingBrand.display_name,
          websiteUrl: `https://${domesticManufacturingWorkspace.domain}`,
          description: '华星精工是一家面向汽车零部件、机器人和新能源装备客户的国内精密制造品牌，提供五轴 CNC、精密压铸、自动化装配和质量追溯服务。',
          industry: 'profile.industryOptions.other',
          brandIdentity: ['可靠交付', '精密制造', '质量可追溯', '工程协同'],
          productsServices: ['五轴 CNC 加工', '汽车零部件制造', '自动化装配', 'IATF 16949 质量体系'],
          targetMarket: 'CN',
        };
      case 'earnedRecommendations':
        return huaxingEarnedInitialRecommendations;
      case 'ownedRecommendations':
        return ownedInitialRecommendations;
      default:
        break;
    }
  }

  switch (name) {
    case 'overview':
      return mockOverviewByCompany[workspaceId] ?? mockOverviewData;
    case 'prompts':
      return mockPromptsByCompany[workspaceId] ?? mockPromptsData;
    case 'domains':
      return {
        total: (mockSourceDomainsByCompany[workspaceId] ?? mockSourceDomains).length,
        page: 1,
        limit: 20,
        data: mockSourceDomainsByCompany[workspaceId] ?? mockSourceDomains,
        domain_types: mockDomainTypeShares,
      };
    case 'urls':
      return {
        total: (mockUrlsByCompany[workspaceId] ?? mockSourceUrls).length,
        page: 1,
        limit: 50,
        data: mockUrlsByCompany[workspaceId] ?? mockSourceUrls,
        domain_types: mockDomainTypeShares,
      };
    case 'gapAnalysis':
      return mockGapAnalysisData;
    case 'insights':
      return mockInsightsData;
    case 'earned':
      return mockEarnedByCompany[workspaceId] ?? mockEarnedData;
    case 'owned':
      return mockOwnedData;
    case 'impact':
      return mockImpactByCompany[workspaceId] ?? mockImpactData;
    case 'profile':
      return mockProfileByCompany[workspaceId] ?? mockProfileData;
    case 'brandProfile':
      return mockBrandProfileByWorkspace[workspaceId] ?? mockBrandProfileDefault;
    case 'earnedRecommendations':
      return earnedInitialRecommendations;
    case 'ownedRecommendations':
      return ownedInitialRecommendations;
    case 'domainMovers':
      return mockDomainMovers;
    case 'domainTypes':
      return mockDomainTypeShares;
    case 'llmPlatforms':
      return { domestic: domesticLLMs, international: internationalLLMs };
    default:
      return undefined;
  }
};

export async function seedPeecIndexedDB() {
  const meta = await getByKey<{ key: string; value: string }>('meta', 'seedVersion');
  if (meta?.value === SEED_VERSION) {
    return;
  }

  const workspaces = [...mockWorkspaces, domesticManufacturingWorkspace];
  await putRecords<WorkspaceItem>('workspaces', workspaces);

  const datasetNames: DatasetName[] = [
    'overview',
    'prompts',
    'domains',
    'urls',
    'gapAnalysis',
    'insights',
    'earned',
    'owned',
    'impact',
    'profile',
    'brandProfile',
    'earnedRecommendations',
    'ownedRecommendations',
    'domainMovers',
    'domainTypes',
    'llmPlatforms',
  ];

  for (const workspaceId of workspaceIds) {
    for (const name of datasetNames) {
      await putDataset(workspaceId, name, buildDataset(workspaceId, name));
    }
  }

  await putRecords<StoredBrand>('brands', workspaceIds.flatMap(withWorkspaceBrands));
  await putRecords<StoredBrandSuggestion>('brandSuggestions', workspaceIds.flatMap(withWorkspaceSuggestions));

  const aiSearchRecords = workspaceIds.flatMap(createWorkspaceAISearchRecords);
  await putRecords<AISearchRecord>('aiSearchRecords', aiSearchRecords);

  await putRecord('meta', { key: 'seedVersion', value: SEED_VERSION, updated_at: new Date().toISOString() });
}

export async function resetPeecIndexedDBSeed() {
  const existingMeta = await getAllFromStore<{ key: string }>('meta');
  if (existingMeta.length > 0) {
    await putRecord('meta', { key: 'seedVersion', value: '', updated_at: new Date().toISOString() });
  }

  await seedPeecIndexedDB();
}